import React from 'react';
import { useJurisdiction, Jurisdiction } from '../context/JurisdictionContext';

interface JurisdictionSwitcherProps {
  compact?: boolean;
}

const JurisdictionSwitcher: React.FC<JurisdictionSwitcherProps> = ({ compact = false }) => {
  const { jurisdiction, jurisdictions, setJurisdiction, isMultiJurisdiction, loading } = useJurisdiction();
  const [open, setOpen] = React.useState(false);

  if (loading) {
    return <span className="text-sm text-gray-400 animate-pulse">Jurisdiction...</span>;
  }

  // No jurisdiction available: render nothing (no-op outside the provider)
  if (!jurisdiction && jurisdictions.length === 0) {
    return null;
  }

  // Single jurisdiction (or none selected): show a static indicator
  if (!isMultiJurisdiction || !jurisdiction) {
    const defaultJ = jurisdictions[0] || { abbreviation: '--', name: 'Unknown' };
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 text-xs font-semibold">
        <i className="fas fa-map-marker-alt mr-1 text-gray-400"></i>
        {defaultJ.abbreviation}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <i className="fas fa-globe-americas mr-2 text-gray-400"></i>
        <span className="font-bold text-gray-900">{jurisdiction.abbreviation}</span>
        <span className="mx-2 text-gray-300">|</span>
        <span className="text-gray-600">{jurisdiction.name}</span>
        <i className={`fas fa-chevron-down ml-2 text-xs text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl z-40 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Switch Jurisdiction</p>
            </div>
            <div className="py-1">
              {jurisdictions.map((jur) => (
                <button
                  key={jur.id}
                  onClick={() => {
                    setJurisdiction(jur as Jurisdiction);
                    setOpen(false);
                    window.location.reload();
                  }}
                  className={`w-full flex items-center px-4 py-3 text-left text-sm transition-colors ${
                    jurisdiction?.id === jur.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-bold w-8">{jur.abbreviation}</span>
                  <span className="flex-1">{jur.name}</span>
                  {jurisdiction?.id === jur.id && <i className="fas fa-check text-indigo-500"></i>}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default JurisdictionSwitcher;